import axios from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

import { env } from "@/lib/env";
import { storage } from "@/lib/storage";
import { errorStore } from "@/stores/error";
import { HttpError } from "@/types";

declare module "axios" {
  export interface AxiosRequestConfig {
    skipAuthRefresh?: boolean;
    suppressGlobalError?: boolean;
  }

  export interface InternalAxiosRequestConfig {
    skipAuthRefresh?: boolean;
    suppressGlobalError?: boolean;
  }
}

const authServerBaseUrl = env.getAuthServerBaseUrl();
const authHttpClient = axios.create({
  baseURL: authServerBaseUrl,
  withCredentials: true,
});

const guacBaseUrl = env.getGuacBaseUrl();
const guacHttpClient = axios.create({
  baseURL: guacBaseUrl,
  withCredentials: true,
});

const setErrorMessage = errorStore.getState().setErrorMessage;
guacHttpClient.interceptors.response.use(
  (response) => response,
  (error: HttpError<{ message?: string }>) => {
    // 401 errors are handled by the auth interceptor below!
    if (
      error.response?.status !== 401 &&
      !error.config?.suppressGlobalError
    ) {
      setErrorMessage(error.response?.data.message ?? "Something went wrong");
    }
    return Promise.reject(error);
  },
);

// Use `axios-auth-refresh` to refresh the access token when it expires.
// https://github.com/Flyrell/axios-auth-refresh?tab=readme-ov-file#usage
const refreshAuthLogic = async () => {
  try {
    const url = `${authServerBaseUrl}/auth/refresh`;
    await authHttpClient.post(url);
    // eslint-disable-next-line
    return Promise.resolve();
  } catch (error) {
    storage.clear();
    window.location.href = "/sign-in";
    // eslint-disable-next-line
    return Promise.reject(error);
  }
};
createAuthRefreshInterceptor(guacHttpClient, refreshAuthLogic, {
  statusCodes: [401],
});

export { authHttpClient, guacHttpClient };
